import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import { useAuth } from "../contexts/AuthContext";
import {
  checkPincodeServiceability,
  createAddress,
  deleteAddress,
  fetchAddresses,
  updateAddress,
} from "../services/accountService";
import { reverseGeocodeLocation } from "../services/locationService";
import { loadGoogleMapsScript } from "../services/googleMapsLoader";
import "../styles/pages/AccountPageSpacing.css";
import "../styles/pages/AddressPage.css";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import {
  LocationAddress,
  PincodeServiceabilityResult,
  UserAddress,
  UserAddressPayload,
} from "../types/store";
import { storeSelectedAddress } from "../utils/selectedAddress";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";

type AddressSource = "MANUAL_ADDRESS" | "CURRENT_LOCATION" | "SEARCH_LOCATION";

type AddressFormState = {
  label: string;
  recipientName: string;
  phone: string;
  houseNumber: string;
  buildingName: string;
  street: string;
  area: string;
  city: string;
  country: string;
  postalCode: string;
  addressMapUrl: string;
  addressSource: AddressSource;
  defaultAddress: boolean;
};

type ResolvedLocation = {
  houseNumber: string;
  buildingName: string;
  street: string;
  area: string;
  city: string;
  country: string;
  postalCode: string;
  streetAddress: string;
  addressMapUrl: string;
};

interface CurrentLocationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (location: ResolvedLocation) => void;
}

const LOCATION_TIMEOUT_MS = 10000;
const DEFAULT_MAP_CENTER = { lat: 20.5937, lng: 78.9629 };

const trimToNull = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const buildGoogleMapsUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;

const joinAddressParts = (...parts: Array<string | null | undefined>) =>
  parts.map((part) => trimToNull(part)).filter(Boolean).join(", ");

const composeStreetAddress = (location: {
  houseNumber?: string | null;
  buildingName?: string | null;
  street?: string | null;
  area?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
}) =>
  joinAddressParts(
    [trimToNull(location.houseNumber), trimToNull(location.buildingName)].filter(Boolean).join(" "),
    location.street,
    location.area,
    location.city,
    location.postalCode,
    location.country,
  );

const resolveLabelMode = (label: string): "home" | "office" | "other" => {
  const normalized = label.trim().toLowerCase();
  if (normalized === "home" || normalized === "office") {
    return normalized;
  }
  return "other";
};

const getCurrentPosition = async (): Promise<{ lat: number; lng: number }> => {
  if (!navigator.geolocation) {
    throw new Error("Your browser does not support location access.");
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      settled = true;
      reject(new Error("Location request timed out. Please allow location access and try again."));
    }, LOCATION_TIMEOUT_MS);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        reject(
          new Error(
            error.code === error.PERMISSION_DENIED
              ? "Location permission was denied. Please allow access and try again."
              : "Unable to read your current location. Please try again.",
          ),
        );
      },
      { enableHighAccuracy: true, timeout: LOCATION_TIMEOUT_MS, maximumAge: 0 },
    );
  });
};

const normalizeLocationAddress = (location: LocationAddress, lat: number, lng: number): ResolvedLocation | null => {
  const city = trimToNull(location.villageTownCity) || trimToNull(location.district);
  const country = trimToNull(location.country) || "India";
  const postalCode = trimToNull(location.pinCode);
  const houseNumber = trimToNull(location.houseNumber) || "";
  const buildingName = trimToNull(location.buildingName) || "";
  const street = trimToNull(location.street) || "";
  const area = trimToNull(location.area) || "";

  if (!city || !postalCode) {
    return null;
  }

  const streetAddress =
    composeStreetAddress({
      houseNumber,
      buildingName,
      street,
      area,
      city,
      postalCode,
      country,
    }) ||
    trimToNull(location.formattedAddress) ||
    joinAddressParts(city, postalCode, country);

  return {
    houseNumber,
    buildingName,
    street,
    area,
    city,
    country,
    postalCode,
    streetAddress,
    addressMapUrl: buildGoogleMapsUrl(lat, lng),
  };
};

const addressToForm = (address: UserAddress, userName = "", phone = ""): AddressFormState => ({
  label: address.label,
  recipientName: address.recipientName || userName,
  phone: address.phone || phone,
  houseNumber: address.houseNumber || "",
  buildingName: address.buildingName || "",
  street: address.street || "",
  area: address.area || "",
  city: address.villageTownCity || address.city || "",
  country: address.country || "India",
  postalCode: address.postalCode || "",
  addressMapUrl: address.addressMapUrl || "",
  addressSource: (address.addressSource as AddressSource) || "MANUAL_ADDRESS",
  defaultAddress: Boolean(address.defaultAddress),
});

const createEmptyForm = (userName = "", phone = ""): AddressFormState => ({
  label: "",
  recipientName: userName,
  phone,
  houseNumber: "",
  buildingName: "",
  street: "",
  area: "",
  city: "",
  country: "India",
  postalCode: "",
  addressMapUrl: "",
  addressSource: "MANUAL_ADDRESS",
  defaultAddress: false,
});

const formToPayload = (formState: AddressFormState): UserAddressPayload => {
  const streetAddress =
    composeStreetAddress({
      houseNumber: formState.houseNumber,
      buildingName: formState.buildingName,
      street: formState.street,
      area: formState.area,
      city: formState.city,
      postalCode: formState.postalCode,
      country: formState.country,
    }) || "";

  return {
    label: trimToNull(formState.label) || "Home",
    recipientName: trimToNull(formState.recipientName) || "",
    phone: trimToNull(formState.phone) || "",
    streetAddress,
    houseNumber: trimToNull(formState.houseNumber),
    buildingName: trimToNull(formState.buildingName),
    street: trimToNull(formState.street),
    area: trimToNull(formState.area),
    villageTownCity: trimToNull(formState.city),
    country: trimToNull(formState.country) || "India",
    addressSource: formState.addressSource,
    city: trimToNull(formState.city) || "",
    postalCode: trimToNull(formState.postalCode) || "",
    addressMapUrl: trimToNull(formState.addressMapUrl),
    defaultAddress: formState.defaultAddress,
  };
};

const CurrentLocationModal: React.FC<CurrentLocationModalProps> = ({ open, onClose, onConfirm }) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const mapDragListenerRef = useRef<any>(null);
  const autoLocatedRef = useRef(false);
  const locatingRef = useRef(false);
  const [pendingLocation, setPendingLocation] = useState<ResolvedLocation | null>(null);
  const [locating, setLocating] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");

  const initializeMap = useCallback(async () => {
    if (!mapContainerRef.current) {
      return null;
    }

    setMapLoading(true);
    try {
      await loadGoogleMapsScript();
      if (!mapContainerRef.current) {
        return null;
      }

      const googleMaps = (window as typeof window & { google?: any }).google?.maps;
      if (!googleMaps) {
        throw new Error("Google Maps is unavailable.");
      }

      const mapOptions = {
        center: DEFAULT_MAP_CENTER,
        zoom: 5,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        gestureHandling: "greedy",
        draggable: true,
        disableDefaultUI: true,
      };

      if (!mapRef.current) {
        mapRef.current = new googleMaps.Map(mapContainerRef.current, mapOptions);
      } else {
        mapRef.current.setOptions(mapOptions);
      }

      if (!mapDragListenerRef.current) {
        mapDragListenerRef.current = mapRef.current.addListener("dragend", async () => {
          const center = mapRef.current?.getCenter?.();
          if (!center) {
            return;
          }

          setLocating(true);
          try {
            const latitude = center.lat();
            const longitude = center.lng();
            const response = await reverseGeocodeLocation(latitude, longitude);
            const normalized = normalizeLocationAddress(response, latitude, longitude);
            if (normalized) {
              setPendingLocation(normalized);
            } else {
              toast.error("We could not read this map location. Please try another point.");
            }
          } catch (error) {
            toast.error(error instanceof Error ? error.message : "Unable to read this map location.");
          } finally {
            setLocating(false);
          }
        });
      }

      setMapError("");
      return mapRef.current;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load Google Maps right now.";
      setMapError(message);
      toast.error(message);
      return null;
    } finally {
      setMapLoading(false);
    }
  }, []);

  const showLocationOnMap = useCallback((lat: number, lng: number) => {
    if (!mapRef.current) {
      return;
    }

    const position = { lat, lng };
    mapRef.current.setCenter(position);
    mapRef.current.setZoom(17);
  }, []);

  const handleUseCurrentLocation = useCallback(async () => {
    if (locatingRef.current) {
      return;
    }

    locatingRef.current = true;
    setLocating(true);
    try {
      // Start the browser GPS request immediately so the permission prompt is shown
      // as soon as the location modal opens, while the map loads in parallel.
      const coordinatesPromise = getCurrentPosition();
      await loadGoogleMapsScript();
      if (!mapRef.current && mapContainerRef.current) {
        await initializeMap();
      }
      const coordinates = await coordinatesPromise;
      showLocationOnMap(coordinates.lat, coordinates.lng);
      const response = await reverseGeocodeLocation(coordinates.lat, coordinates.lng);
      const normalized = normalizeLocationAddress(response, coordinates.lat, coordinates.lng);
      if (!normalized) {
        toast.error("We found your location, but could not read a complete address. Please fill it manually.");
        return;
      }
      setPendingLocation(normalized);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to read your current location.");
    } finally {
      locatingRef.current = false;
      setLocating(false);
    }
  }, [initializeMap, showLocationOnMap]);

  useEffect(() => {
    if (!open) {
      setPendingLocation(null);
      setLocating(false);
      locatingRef.current = false;
      setMapLoading(false);
      setMapError("");
      mapRef.current = null;
      if (mapDragListenerRef.current) {
        mapDragListenerRef.current.remove();
        mapDragListenerRef.current = null;
      }
      autoLocatedRef.current = false;
      return;
    }

    if (!autoLocatedRef.current) {
      autoLocatedRef.current = true;
      void handleUseCurrentLocation();
    }

    return undefined;
  }, [handleUseCurrentLocation, initializeMap, open]);

  if (!open) {
    return null;
  }

  return (
    <div className="address-modal" role="presentation">
      <div
        className="address-modal__dialog address-location-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="current-location-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="address-modal__close address-modal__close--absolute" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="address-location-modal__copy">
          <span className="eyebrow">Address location</span>
          <h2 id="current-location-title">Select current location</h2>
          <p className="address-location-modal__note">
            Use your current GPS location first. After confirming, complete the remaining address details.
          </p>
        </div>

        {pendingLocation ? (
          <div className="address-location-modal__preview" aria-live="polite">
            <strong>{pendingLocation.streetAddress}</strong>
            <small>
              {pendingLocation.city}, {pendingLocation.postalCode}
            </small>
          </div>
        ) : null}

        <div className="address-location-map is-compact">
          <div className="address-location-map__canvas-wrap">
            <div className="address-location-map__canvas" ref={mapContainerRef} aria-label="Current location map" />
            <div className="address-location-map__pin" aria-hidden="true">
              <span className="address-location-map__pin-drop" />
              <span className="address-location-map__pin-shadow" />
            </div>
            {mapError ? (
              <div className="address-location-map__error" aria-live="polite">
                <strong>Map could not load</strong>
                <span>{mapError}</span>
              </div>
            ) : null}
            {mapLoading || locating ? (
              <div className="address-location-map__loading" aria-live="polite">
                <span className="address-location-map__loading-ring" aria-hidden="true" />
                <span className="address-location-map__loading-copy">
                  {locating ? "Finding your current location..." : "Loading map..."}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="address-location-modal__confirm">
          <button
            type="button"
            className="button address-location-map__button--current"
            onClick={handleUseCurrentLocation}
            disabled={locating}
          >
            {locating ? "Finding current location..." : "Select current location"}
          </button>
          <button
            type="button"
            className="address-location-modal__confirm-button"
            onClick={() => pendingLocation && onConfirm(pendingLocation)}
            disabled={!pendingLocation || locating}
          >
            Confirm location
          </button>
        </div>
      </div>
    </div>
  );
};

const AddressPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkingPincode, setCheckingPincode] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState<PincodeServiceabilityResult | null>(null);
  const [addressEditorOpen, setAddressEditorOpen] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | "new" | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<UserAddress | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [formState, setFormState] = useState<AddressFormState>(
    createEmptyForm(user?.fullName || "", user?.phoneNumber || ""),
  );
  const routeState = location.state as
    | {
        returnTo?: string;
        source?: string;
        openEditor?: boolean;
        openLocationPickerFirst?: boolean;
        resumeServiceBookingStep?: 2 | 3 | 4 | 5;
      }
    | null
    | undefined;

  useBodyScrollLock(addressEditorOpen || locationPickerOpen);

  useEffect(() => {
    void loadGoogleMapsScript().catch(() => null);
  }, []);

  useEffect(() => {
    const loadAddresses = async () => {
      try {
        const response = await fetchAddresses();
        setAddresses(response);
      const firstAddress = response.find((address) => address.defaultAddress) ?? response[0] ?? null;
      if (firstAddress) {
        setSelectedAddressId(firstAddress.id);
      }
      } catch {
        toast.error("Unable to load your saved addresses right now.");
      } finally {
        setLoading(false);
      }
    };

    void loadAddresses();
  }, []);

  useEffect(() => {
    if (!routeState?.openEditor) {
      return;
    }

    openAddressEditor(null);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeState?.openEditor, navigate, location.pathname]);

  useEffect(() => {
    if (!routeState?.openLocationPickerFirst) {
      return;
    }

    setSelectedAddress(null);
    setSelectedAddressId("new");
    setFormState(createEmptyForm(user?.fullName || "", user?.phoneNumber || ""));
    setAddressEditorOpen(false);
    setLocationPickerOpen(true);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeState?.openLocationPickerFirst, navigate, location.pathname]);

  useEffect(() => {
    const activePostalCode = formState.postalCode.trim();
    if (!/^\d{6}$/.test(activePostalCode)) {
      setPincodeStatus(null);
      setCheckingPincode(false);
      return;
    }

    let cancelled = false;
    setCheckingPincode(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await checkPincodeServiceability(activePostalCode);
        if (!cancelled) {
          setPincodeStatus(result);
        }
      } catch {
        if (!cancelled) {
          setPincodeStatus({
            pincode: activePostalCode,
            serviceable: false,
            message: "Unable to verify this pincode right now.",
          });
        }
      } finally {
        if (!cancelled) {
          setCheckingPincode(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [formState.postalCode]);

  const openAddressEditor = (address: UserAddress | null) => {
    setSelectedAddress(address);
    setSelectedAddressId(address ? address.id : "new");
    if (address) {
      setFormState(addressToForm(address, user?.fullName || "", user?.phoneNumber || ""));
    } else {
      setFormState(createEmptyForm(user?.fullName || "", user?.phoneNumber || ""));
    }
    setAddressEditorOpen(true);
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    const nextValue = type === "checkbox" ? (event.target as HTMLInputElement).checked : value;
    setFormState((current) => ({
      ...current,
      [name]: nextValue,
    } as AddressFormState));
  };

  const handleSelectAddress = (address: UserAddress) => {
    setSelectedAddressId(address.id);
    setFormState(addressToForm(address, user?.fullName || "", user?.phoneNumber || ""));
    storeSelectedAddress(user, address);
    setMenuOpenId(null);
    setAddressEditorOpen(false);
    setLocationPickerOpen(false);
    const returnTo = routeState?.returnTo;
    if (returnTo) {
      navigate(
        returnTo,
          returnTo === "/services"
          ? {
              replace: true,
              state: {
                resumeServiceBooking: true,
                resumeServiceBookingStep: routeState?.resumeServiceBookingStep ?? 3,
              },
            }
          : { replace: true },
      );
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!window.confirm("Delete this address?")) {
      return;
    }

    try {
      await deleteAddress(addressId);
      const remaining = addresses.filter((address) => address.id !== addressId);
      setAddresses(remaining);

      if (selectedAddressId === addressId) {
        const fallback = remaining.find((address) => address.defaultAddress) ?? remaining[0] ?? null;
        if (fallback) {
          setSelectedAddressId(fallback.id);
          setSelectedAddress(fallback);
          setFormState(addressToForm(fallback, user?.fullName || "", user?.phoneNumber || ""));
          storeSelectedAddress(user, fallback);
        } else {
          setSelectedAddressId(null);
          setSelectedAddress(null);
          storeSelectedAddress(user, null);
        }
      }

      setMenuOpenId(null);
      setAddressEditorOpen(false);
      setLocationPickerOpen(false);
      toast.success("Address deleted successfully.");
    } catch {
      toast.error("Unable to delete this address right now.");
    } finally {
      setMenuOpenId(null);
    }
  };

  const persistAddress = async (nextFormState: AddressFormState) => {
    if (saving) {
      return false;
    }

    if (!trimToNull(nextFormState.recipientName) || !trimToNull(nextFormState.phone)) {
      toast.error("Recipient name and phone number are required.");
      return false;
    }

    if (!trimToNull(nextFormState.city)) {
      toast.error("Village, town, or city is required.");
      return false;
    }

    if (!trimToNull(nextFormState.postalCode)) {
      toast.error("PIN code is required.");
      return false;
    }

    setSaving(true);
    const payload = {
      ...formToPayload(nextFormState),
      label: trimToNull(selectedAddress?.label) || "Home",
    };

    try {
      if (typeof selectedAddressId === "number") {
        const updated = await updateAddress(selectedAddressId, payload);
        setAddresses((current) => current.map((address) => (address.id === updated.id ? updated : address)));
        setSelectedAddressId(updated.id);
        setFormState(addressToForm(updated, user?.fullName || "", user?.phoneNumber || ""));
        storeSelectedAddress(user, updated);
      } else {
        const created = await createAddress(payload);
        setAddresses((current) => [...current, created]);
        setSelectedAddressId(created.id);
        setFormState(addressToForm(created, user?.fullName || "", user?.phoneNumber || ""));
        storeSelectedAddress(user, created);
      }

      setAddressEditorOpen(false);
      const returnTo = routeState?.returnTo;
      if (returnTo) {
        navigate(
          returnTo,
          returnTo === "/services"
          ? {
              replace: true,
              state: {
                  resumeServiceBooking: true,
                  resumeServiceBookingStep: routeState?.resumeServiceBookingStep ?? 3,
                },
              }
            : { replace: true },
        );
      }
      toast.success("Address saved successfully.");
      return true;
    } catch {
      toast.error("Unable to save this address right now.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await persistAddress(formState);
  };

  const applyCurrentLocation = (resolvedLocation: ResolvedLocation) => {
    setSelectedAddress(null);
    setSelectedAddressId("new");
    setFormState((current) => ({
      ...current,
      houseNumber: resolvedLocation.houseNumber,
      buildingName: resolvedLocation.buildingName,
      street: resolvedLocation.street,
      area: resolvedLocation.area,
      city: resolvedLocation.city,
      country: resolvedLocation.country,
      postalCode: resolvedLocation.postalCode,
      addressMapUrl: resolvedLocation.addressMapUrl,
      addressSource: "CURRENT_LOCATION",
    }));
    setLocationPickerOpen(false);
    setAddressEditorOpen(true);
  };

  return (
    <section className={`shell section page-section address-page ${addressEditorOpen ? "is-editing" : ""}`}>
      <MobilePageHeader title="Saved addresses" />

      <div className="page-header address-page__desktop-header">
        <span className="eyebrow">Address</span>
        <h1>Saved addresses</h1>
      </div>

      <div className="address-layout">
        <aside className="store-card address-list-card">
          <button
            type="button"
            className="address-add-row"
            onClick={() => {
              setSelectedAddress(null);
              setSelectedAddressId("new");
              setFormState(createEmptyForm(user?.fullName || "", user?.phoneNumber || ""));
              setAddressEditorOpen(false);
              setLocationPickerOpen(true);
            }}
          >
            <span>+</span>
            <strong>Add New</strong>
            <b aria-hidden="true">›</b>
          </button>

          <h2>Saved addresses</h2>

          {loading ? (
            <p className="address-list-card__empty">Loading your saved addresses...</p>
          ) : addresses.length ? (
            <div className="address-list">
              {addresses.map((address) => (
                <article
                  key={address.id}
                  className={`address-list__item ${selectedAddressId === address.id ? "is-active" : ""}`}
                >
                  <div className="address-list__icon" aria-hidden="true">
                    {resolveLabelMode(address.label) === "office"
                      ? "🏢"
                      : resolveLabelMode(address.label) === "other"
                        ? "📍"
                        : "🏠"}
                  </div>
                  <button type="button" className="address-list__body" onClick={() => handleSelectAddress(address)}>
                    <strong>{address.recipientName}</strong>
                    <span>{address.streetAddress}</span>
                    <small>
                      {address.villageTownCity || address.city}, {address.postalCode}
                    </small>
                    <em>{address.phone}</em>
                  </button>
                  <div className="address-list__actions">
                    <button
                      type="button"
                      className="address-list__menu-button"
                      aria-label="Address options"
                      onClick={() => setMenuOpenId((current) => (current === address.id ? null : address.id))}
                    >
                      ⋮
                    </button>
                    {menuOpenId === address.id ? (
                      <div className="address-list__menu">
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpenId(null);
                            openAddressEditor(address);
                          }}
                        >
                          Edit
                        </button>
                        <button type="button" onClick={() => void handleDeleteAddress(address.id)}>
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="address-list-card__empty">No saved addresses yet. Add your first address here.</p>
          )}
        </aside>
      </div>

      {addressEditorOpen ? (
        <div className="address-modal" role="presentation">
          <div
            className="address-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="address-editor-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="address-modal__header">
              <div>
                <span className="eyebrow">Address editor</span>
                <h2 id="address-editor-title">{selectedAddress ? "Edit address" : "Add address"}</h2>
              </div>
              <button
                type="button"
                className="address-modal__close"
                onClick={() => setAddressEditorOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <form className="form-card address-form-card" onSubmit={handleSave}>
              <div className="form-grid address-form-grid">
                <label>
                  Recipient name
                  <input name="recipientName" value={formState.recipientName} onChange={handleFormChange} required />
                </label>

                <label>
                  Phone number
                  <input name="phone" value={formState.phone} onChange={handleFormChange} required />
                </label>

                <label>
                  House / Building number
                  <input name="houseNumber" value={formState.houseNumber} onChange={handleFormChange} />
                </label>

                <label>
                  Building name
                  <input name="buildingName" value={formState.buildingName} onChange={handleFormChange} />
                </label>

                <label>
                  Street / Road
                  <input name="street" value={formState.street} onChange={handleFormChange} />
                </label>

                <label>
                  Area / Sub-locality
                  <input name="area" value={formState.area} onChange={handleFormChange} />
                </label>

                <label>
                  Village / Town / City
                  <input name="city" value={formState.city} onChange={handleFormChange} required />
                </label>

                <label>
                  PIN code
                  <input name="postalCode" value={formState.postalCode} onChange={handleFormChange} required />
                  {pincodeStatus && !pincodeStatus.serviceable ? (
                    <small className="address-pincode-status is-unserviceable">
                      Not serviceable
                    </small>
                  ) : null}
                </label>

                <label className="checkout-inline-check address-default-check form-grid__wide">
                  <input
                    type="checkbox"
                    name="defaultAddress"
                    checked={formState.defaultAddress}
                    onChange={handleFormChange}
                  />
                  <span>Set as default address</span>
                </label>
              </div>

              <div className="address-form-card__actions">
                {selectedAddress ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setFormState(addressToForm(selectedAddress, user?.fullName || "", user?.phoneNumber || ""));
                      setAddressEditorOpen(false);
                    }}
                    >
                      Cancel
                    </button>
                ) : null}
                <button className="button" type="submit" disabled={saving} aria-busy={saving}>
                  {saving ? "Saving..." : "Save address"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <CurrentLocationModal
        open={locationPickerOpen}
        onClose={() => {
          setLocationPickerOpen(false);
        }}
        onConfirm={applyCurrentLocation}
      />
    </section>
  );
};

export default AddressPage;
